'use client';

import posthog from 'posthog-js';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';

import { IS_ANALYTICS_ENABLED } from '@cineamo/composables/src/providers/analytics-environment';
import AnalyticsProvider from '@cineamo/composables/src/providers/analyticsProvider';
import { posthogTracker } from '@cineamo/composables/src/providers/posthog-analytics-tracker';
import { posthogConsentConfig, usePostHogConsentSync } from '@cineamo/composables/src/providers/posthog-consent';
import { registerPostHogSuperProperties } from '@cineamo/composables/src/providers/posthog-super-properties';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

/**
 * The booking flow's analytics, with PostHog initialized here rather than in the app's root
 * `Providers` — the one deliberate difference from the web app.
 *
 * The cinema websites carry far more traffic than they do bookings: a root init would spend
 * PostHog's event and flag-request quota on every program and film page of every customer domain,
 * for data we have no question about. Mounting it on the booking layout means we pay for exactly
 * the funnel we measure. For the same reason autocapture and `$pageview` stay off: this client
 * exists to carry the booking funnel's own events and the seat map's experiment assignment, nothing
 * else. Vercel Analytics remains the source for page-level traffic, as before.
 *
 * `eventPrefix` matches the web app's booking layout, so one PostHog experiment can span both.
 */
const BookingAnalyticsProvider: React.FC<PropsWithChildren<{ overallData: Record<string, string> }>> = ({
    overallData,
    children
}) => {
    usePostHogConsentSync();

    useEffect(() => {
        // POSTHOG_KEY is re-checked purely to narrow it for `init` — IS_ANALYTICS_ENABLED covers it.
        if (!IS_ANALYTICS_ENABLED || !POSTHOG_KEY) return;

        if (!posthog.__loaded) {
            posthog.init(POSTHOG_KEY, {
                api_host: POSTHOG_HOST,
                autocapture: false,
                capture_pageview: false,
                capture_pageleave: false,
                ...posthogConsentConfig(),
                disable_session_recording: true
            });
        }

        return registerPostHogSuperProperties();
    }, []);

    const trackers = useMemo(() => [posthogTracker], []);

    return (
        <AnalyticsProvider
            overallData={overallData}
            eventPrefix="OT"
            trackers={trackers}>
            {children}
        </AnalyticsProvider>
    );
};

export default BookingAnalyticsProvider;
